import { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import styles from "./AppsMenu.module.scss";
import Apps from "../../assets/icons/Apps.svg?react";
import DRTC from "../../assets/icons/DRTCLogo.svg?react";
import ObesiD from "../../assets/icons/ObesidLogo.svg?react";
import AIDH from "../../assets/icons/AIDHLogo.svg?react";
import ChevronRight from "../../assets/icons/ChevronRight.svg?react";
import useWhitelabelAvailability from "../../hooks/useWhitelabelAvailability";
import useAuth from "../../hooks/useAuth";
import useSalesforce from "../../hooks/useSalesforce";
import { usePortal } from "../../hooks/usePortal";
import { getReferrer } from "../../utils/authUtils";

const apps = [
  {
    id: "obesid",
    targetApp: "obesity",
    name: "ObesiD",
    Component: ObesiD,
    width: 140,
    subscriptionId: [2151, 2170],
    url: import.meta.env.VITE_OBESID_APP_BASE_URL || "https://obesiD.tg.org.au",
  },
  {
    id: "drtc",
    targetApp: "crush",
    name: "DRTC",
    Component: DRTC,
    width: 140,
    subscriptionId: [3151, 3170],
    url: import.meta.env.VITE_DRTC_APP_BASE_URL || "https://dontrushtocrush.tg.org.au",
  },
  {
    id: "aidh",
    targetApp: "aidh",
    name: "AIDH",
    Component: AIDH,
    width: 140,
    subscriptionId: [4151, 4170],
    url: import.meta.env.VITE_AIDH_APP_BASE_URL || "https://aidh.tg.org.au",
  },
];

const AppsMenu = ({ iconSize = 32 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isSubscribeEnabled } = useSalesforce();
  const menuRef = useRef(null);
  const { isWhitelabelEnabled, drtcAvailableFrom } = useWhitelabelAvailability();
  const { availableAppSlugs = [], isInstitution = false } = useAuth();
  const { isPortalEnabled, portalUrl } = usePortal();

  const hasAccess = useCallback(
    (targetApp) => availableAppSlugs.includes(targetApp),
    [availableAppSlugs],
  );

  const toggleMenu = useCallback(() => setIsOpen((prev) => !prev), []);

  const handleAppClick = useCallback(
    (app, enabled) => {
      if (!enabled) return; // Coming soon — do nothing

      if (!hasAccess(app.targetApp)) {
        window.location.href = isPortalEnabled ? `${portalUrl}/subscribe` : "/subscribe";
        return;
      }

      // Pass target app context across domains/tabs.
      const appUrl = new URL(app.url, window.location.origin);
      appUrl.searchParams.set("targetApp", app.targetApp);
      if (isInstitution) {
        const savedReferrer = getReferrer();
        if (savedReferrer) {
          appUrl.searchParams.set("r", savedReferrer);
        }
      }
      window.location.href = appUrl.toString();
      setIsOpen(false);
    },
    [hasAccess, isInstitution, isPortalEnabled, portalUrl],
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className={styles.appsMenu} ref={menuRef}>
      <button
        className={styles.appsButton}
        onClick={toggleMenu}
        aria-label="Switch Applications"
        aria-expanded={isOpen}
      >
        <Apps width={iconSize} height={iconSize} />
      </button>

      {isOpen && (
        <div className={styles.dropdown} role="menu">
          <div className={styles.dropdownContent}>
            {apps.map((app) => {
              const enabled = isWhitelabelEnabled(app.id);
              const subscribed = enabled && hasAccess(app.targetApp);
              const { Component } = app;

              return (
                <button
                  key={app.id}
                  className={`${styles.appItem} ${!enabled ? styles.appItemDisabled : ""}`}
                  onClick={() => handleAppClick(app, enabled)}
                  role="menuitem"
                  disabled={enabled && !isSubscribeEnabled && !subscribed}
                  aria-disabled={!enabled}
                  tabIndex={!enabled ? -1 : 0}
                >
                  <div className={styles.appLogo}>
                    <Component width={app.width} height="auto" />
                  </div>

                  <div className={styles.appAction}>
                    {!enabled ? (
                      <div className={styles.comingSoonWrapper}>
                        <span className={styles.comingSoonBadge}>Coming soon</span>
                        {app.id === "drtc" && drtcAvailableFrom && (
                          <span className={styles.comingSoonTooltip}>
                            Subscriptions available from {drtcAvailableFrom}
                          </span>
                        )}
                      </div>
                    ) : subscribed ? (
                      <div className={styles.appChevron}>
                        <ChevronRight />
                      </div>
                    ) : (
                      <span className={styles.subscribeButton}>Subscribe</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

AppsMenu.propTypes = {
  iconSize: PropTypes.number,
};

export default AppsMenu;
